import React from "react";
import { useRoutes, Navigate, useParams, type RouteObject } from "react-router-dom";
import Dashboard from "../pages/Admin/Dashboard";
import Service from "../pages/Admin/Service";
import { LayoutProvider } from "../theme/LayoutContext";
import SingLayout from "../sing/components/Layout/Layout";
import ManageCouples from "../pages/Admin/ManageCouples";
import ManageRetreat from "../pages/Admin/Retreats/ManageRetreat";
import ProjectedRetreatForm from "../pages/Admin/Retreats/ProjectedRetreatForm";
import RetreatSetupWorkspace from "../pages/Admin/Retreats/RetreatSetupWorkspace";
import Talks from "../pages/Admin/Talks";
import ParishCategories from "../pages/Admin/ParishCategories";
import Deaneries from "../pages/Admin/Deaneries";
import Roles from "../pages/Admin/Roles";
import SendEmail from "../pages/Admin/SendEmail";
import MyProfile from "../pages/Admin/MyProfile";
import Perish from "../pages/Admin/Parish";
import SearchData from "../pages/Admin/SearchData";
import Help from "../pages/Admin/Help";
import RetretRoaster from "../pages/Admin/Retreats/RetreatRoster";
import QuickReport from "../pages/Admin/QuickReport";
import CoupleUpdate from "../pages/Common/CoupleUpdate";
import ThankYou from "../pages/Common/ThankYou";
import LogReport from "../pages/Admin/LogReport";
import AccessSuperAdmin from "../pages/Admin/AccessSuperAdmin";
import { RegistrationRetreat } from "../pages/Auth/RegistrationRetreat";
import Directory from "../pages/Auth/Directory";
import Program from "../pages/Auth/Program";
import { CreateProgram } from "../pages/Admin/Retreats/CreateProgram";
import { CreateDirectory } from "../pages/Admin/Retreats/CreateDirectory";
import RetreatBackUp from "../pages/Admin/Retreats/RetreatBackup";
import { UpdateCouple } from "../pages/Couple/UpdateCouple";
import { getAuthUser } from "../hooks/useAuthUser";
import AdminDashboard from "../pages/Admin/AdminDashboard";
import ParticipatedInRetreats from "../pages/Couple/ParticipatedInRetreats";
import Tickets from "../pages/TicketSystem/Tickets";
import CreateTicket from "../pages/TicketSystem/CreateTicket";
import TicketReply from "../pages/TicketSystem/TicketReply";
import NewsletterLink from "../pages/Auth/NewsletterLink";
import UpcomingRetreats from "../pages/Auth/UpcomingRetreats";
import { RegisterWaitList } from "../pages/WaitList/RegisterWaitList";
import ClosuresList from "../pages/RetreatClosure/ClosuresList";
import PriestList from "../pages/Priest/PriestList";
import BuildMyTeam from "../pages/Admin/Retreats/BuildMyTeam";
import SampleContent from "../pages/Admin/SampleContent";
import type { RouterWithLogoutProps } from "../types/layout";
import type { LoveStrongUser } from "../types/api";

type AdminRouterProps = {
  logoutUser?: RouterWithLogoutProps["logoutUser"] | null;
};

function RedirectToRetreatSetup() {
  const { id } = useParams();
  return <Navigate to={`/retreat-setup/${id}`} replace />;
}

const AdminRouter = ({ logoutUser = null }: AdminRouterProps): React.ReactElement => {
  let userData = getAuthUser<LoveStrongUser>();

  const commonRotes: RouteObject[] = [
    { path: "/dashboard", element: <Dashboard /> },
    { path: "/quickReport", element: <QuickReport></QuickReport> },
    { path: "/help", element: <Help /> },
    { path: "/search_data", element: <SearchData /> },
    { path: "/service", element: <Service /> },
    { path: "/talks", element: <Talks /> },
    { path: "/parishCategories", element: <ParishCategories /> },
    { path: "/deaneries", element: <Deaneries /> },
    { path: "/roles", element: <Roles /> },
    { path: "/sendEmail", element: <SendEmail /> },
    { path: "/manageCouples", element: <ManageCouples /> },
    { path: "/manageRetreat", element: <ManageRetreat /> },
    { path: "/retreats/project", element: <ProjectedRetreatForm /> },
    { path: "/retreats/:id/project", element: <ProjectedRetreatForm /> },
    { path: "/retreat-setup/:id", element: <RetreatSetupWorkspace /> },
    { path: "/my-profile", element: <MyProfile /> },
    { path: "/parish", element: <Perish /> },
    { path: "/retreatRoster", element: <RetretRoaster /> },
    { path: "/couple-update/:coupleId", element: <CoupleUpdate /> },
    { path: "/thankyou", element: <ThankYou /> },
    { path: "/logreport", element: <LogReport /> },
    { path: "/Access-SuperAdmin", element: <AccessSuperAdmin /> },
    { path: "/registration-retreat/:id", element: <RegistrationRetreat /> },
    { path: "/register-new-retreat", element: <Navigate to="/retreats/project" replace /> },
    { path: "/directory/:id", element: <Directory /> },
    { path: "/program/:id", element: <Program /> },
    { path: "/new-retreats", element: <Navigate to="/manageRetreat" replace /> },
    { path: "/priest-list", element: <PriestList /> },
    { path: "/missing-data", element: <ManageCouples /> },
    {
      path: "/retreat-registrations",
      element: <Navigate to="/manageRetreat" replace />,
    },
    {
      path: "/create-retreat",
      element: <Navigate to="/retreats/project" replace />,
    },
    {
      path: "/create-retreat/:id",
      element: <RedirectToRetreatSetup />,
    },
    {
      path: "/create-program/:id",
      element: <CreateProgram />,
    },
    {
      path: "/create-directory/:id",
      element: <CreateDirectory />,
    },
    {
      path: "/retreat-backup",
      element: <RetreatBackUp />,
    },
    { path: "/participated-in-retreats", element: <ParticipatedInRetreats /> },
    {
      path: "/admin-dashboard",
      element: <AdminDashboard />,
    },
    { path: "/update-couple", element: <UpdateCouple /> },
    { path: "/tickets", element: <Tickets /> },
    { path: "/create-ticket", element: <CreateTicket /> },
    { path: "/ticketReply/:id", element: <TicketReply /> },
    { path: "/news-letter-link", element: <NewsletterLink /> },
    { path: "/upcoming-retreats", element: <UpcomingRetreats /> },
    { path: "/register-waitList/:id", element: <RegisterWaitList /> },
    { path: "/retreat-closure-list", element: <ClosuresList /> },
    { path: "/build-my-team", element: <BuildMyTeam /> },
    { path: "/sample-content", element: <SampleContent /> },
  ];

  if (
    (userData && userData.userRole === "SUBADMIN") ||
    (userData.userRole === "ADMIN" && userData.isDisabled === 1)
  ) {
    commonRotes.push({ path: "/*", element: <Navigate to="/update-couple" /> });
  } else {
    commonRotes.push({ path: "/*", element: <Navigate to="/dashboard" /> });
  }

  const route = useRoutes(commonRotes);

  let pathName: any = route;
  if (
    pathName.props.match.route.path === "/couple-update/:coupleId" ||
    pathName.props.match.route.path === "/thankyou" ||
    pathName.props.match.route.path === "/registration-retreat/:id" ||
    pathName.props.match.route.path === "/directory/:id" ||
    pathName.props.match.route.path === "/program/:id" ||
    pathName.props.match.route.path === "/create-ticket" ||
    pathName.props.match.route.path === "/ticketReply/:id" ||
    pathName.props.match.route.path === "/news-letter-link" ||
    pathName.props.match.route.path === "/register-waitList/:id"
  ) {
    return <>{route}</>;
  }
  return (
    <LayoutProvider>
      <SingLayout logoutUser={logoutUser}>{route}</SingLayout>
    </LayoutProvider>
  );
};
export default AdminRouter;
